import React, { useState, useEffect, useContext } from "react";
import { Container, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsHeartbreak, BsChevronRight, BsCart3 } from "react-icons/bs";
import { FiX } from "react-icons/fi";
import axios from "axios";
import MyNavbar from "../../../../components/general/mynavbar/MyNavbar";
import BottomNavigation from "../../../../components/general/bottomnavigation/BottomNavigation";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../../assets/css/customer/subscreens/account/FavoritesPage.css";
import AppContext from "../../../../contexts/AppContext";
import LoadingScreen from "../../../../components/general/animations/Animations.js";

const getStockClass = (qty) => {
  if (qty > 10) return "stock-badge-instock";
  if (qty > 0) return "stock-badge-lowstock";
  return "stock-badge-outofstock";
};

// Wishlist doesn't store the customer's specific selection, so we show
// the product's available variant options (not the exact one picked).
// Colors are rendered as small color swatches (dots), sizes as plain text.
const renderVariantOptions = (product) => {
  const colors = product?.variant?.colors
    ? product.variant.colors.split(",").filter(Boolean)
    : [];
  const sizes = product?.variant?.sizes
    ? product.variant.sizes.split(",").filter(Boolean)
    : [];

  if (colors.length === 0 && sizes.length === 0) return null;

  return (
    <div className="wishlist-variant-options">
      {colors.length > 0 && (
        <span className="wishlist-color-group">
          Colors:
          {colors.map((c, i) => (
            <span
              key={i}
              className="wishlist-color-dot"
              style={{ backgroundColor: c.trim() }}
              title={c.trim()}
            />
          ))}
        </span>
      )}
      {sizes.length > 0 && (
        <span className="wishlist-size-group">Sizes: {sizes.join(", ")}</span>
      )}
    </div>
  );
};

const formatDateAdded = (item) => {
  const raw = item?.date_added || item?.created_at;
  if (!raw) return "-";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-PK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const FavoritesPage = () => {
  const navigate = useNavigate();
  const {
    baseUrl,
    baseUrlForFiles,
    customer,
    customerAuthToken,
    clearUnread,
    incrementUnread,
    setSelectedProductForCustomer, // used to open ProductDetail with this product
  } = useContext(AppContext);

  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCartId, setAddingToCartId] = useState(null);

  const customerId = customer?.id;

  const viewWishlist = () => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append("customer_id", customerId);
    axios
      .post(`${baseUrl}/customer/wishlist/viewWishlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((response) => {
        setWishlist(response.data.wishlist || []);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load wishlist. Please try again");
        setLoading(false);
      });
  };

  useEffect(() => {
    viewWishlist();
    clearUnread("favourites");
  }, []);

  const deleteWishlist = (id) => {
    const formdata = new FormData();
    formdata.append("wishlist_id", id);
    axios
      .post(`${baseUrl}/customer/wishlist/deleteWishlist`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        viewWishlist();
      })
      .catch((error) => console.log(error));
  };

  // ── Navigate to full Product Detail page for this wishlist item ──
  const handleViewProduct = (item) => {
    if (!item?.product) return;
    setSelectedProductForCustomer(item.product);
    navigate("/product");
  };

  // ── Add to Cart: simple direct add + toast ──
  const handleAddToCart = (item) => {
    const product = item.product;
    if (!product?.id) return;

    // Guard: never allow adding an out-of-stock product
    if (Number(product.stock_quantity) <= 0) {
      toast.error("This product is out of stock");
      return;
    }

    if (!customerId) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    setAddingToCartId(item.id);

    const formdata = new FormData();
    formdata.append("chcustomer_id", customerId);
    formdata.append("chproduct_id", product.id);
    formdata.append("price", product.price);
    formdata.append("subtotal", product.price);
    formdata.append("quantity", 1);
    formdata.append("isPurchased", 0);
    formdata.append("isCustomized", 0);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then(() => {
        incrementUnread && incrementUnread("cart");
        toast.success("Added to Cart");
      })
      .catch(() => toast.error("Failed to add to cart"))
      .finally(() => setAddingToCartId(null));
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <MyNavbar />
      <ToastContainer />

      <section className="favorites-section">
        <Container className="favorites-container">
          <div className="favorites-shell-wrapper">
            <div className="favorites-section-header">
              <h2 className="favorites-section-title">Wishlist</h2>
              <div className="favorites-breadcrumb">
                <span
                  className="breadcrumb-link"
                  onClick={() => navigate("/account")}
                >
                  Account
                </span>
                <BsChevronRight className="breadcrumb-separator" />
                <span className="breadcrumb-current">Wishlist</span>
              </div>
            </div>

            {wishlist.length > 0 ? (
              <div className="wishlist-table-wrapper">
                <table className="wishlist-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Date Added</th>
                      <th>Stock Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlist.map((item) => {
                      const inStock = Number(item.product?.stock_quantity) > 0;
                      return (
                        <tr
                          key={item.id}
                          className="favorite-product-row"
                          onClick={() => handleViewProduct(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="remove-col">
                            <button
                              className="btn-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteWishlist(item.id);
                              }}
                              title="Remove from wishlist"
                            >
                              <FiX />
                            </button>
                          </td>
                          <td className="product-col">
                            <div className="product-info-cell">
                              <img
                                src={`${baseUrlForFiles}/${item.product?.images?.[0]?.image_path}`}
                                alt={item.product?.product_name}
                                className="product-thumb"
                              />
                              <div className="product-details">
                                <h4>{item.product?.product_name}</h4>
                                {renderVariantOptions(item.product)}
                              </div>
                            </div>
                          </td>
                          <td className="price-col">
                            Rs {item.product?.price}
                          </td>
                          <td className="date-col">{formatDateAdded(item)}</td>
                          <td className="stock-col">
                            <Badge
                              className={`stock-badge ${getStockClass(item.product?.stock_quantity)}`}
                            >
                              {item.stock_status}
                            </Badge>
                          </td>
                          <td className="action-col">
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!inStock) {
                                  toast.error("This product is out of stock");
                                }
                              }}
                            >
                              <button
                                className="btn-add-cart"
                                disabled={
                                  !inStock || addingToCartId === item.id
                                }
                                style={
                                  !inStock ? { pointerEvents: "none" } : {}
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToCart(item);
                                }}
                              >
                                <BsCart3 size={14} />
                                {addingToCartId === item.id
                                  ? "Adding..."
                                  : "Add to Cart"}
                              </button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="wishlist-mobile">
                  {wishlist.map((item) => {
                    const inStock = Number(item.product?.stock_quantity) > 0;
                    return (
                      <div
                        key={item.id}
                        className="wishlist-mobile-card"
                        onClick={() => handleViewProduct(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <button
                          className="btn-remove-mobile"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteWishlist(item.id);
                          }}
                          title="Remove"
                        >
                          <FiX />
                        </button>
                        <div className="mobile-card-content">
                          <img
                            src={`${baseUrlForFiles}/${item.product?.images?.[0]?.image_path}`}
                            alt={item.product?.product_name}
                            className="mobile-product-img"
                          />
                          <div className="mobile-product-info">
                            <h4>{item.product?.product_name}</h4>
                            {renderVariantOptions(item.product)}
                            <p className="mobile-price">
                              Rs {item.product?.price}
                            </p>
                            <p className="mobile-date">
                              Added: {formatDateAdded(item)}
                            </p>
                            <Badge
                              className={`stock-badge mobile-stock ${getStockClass(item.product?.stock_quantity)}`}
                            >
                              {item.stock_status}
                            </Badge>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!inStock) {
                                  toast.error("This product is out of stock");
                                }
                              }}
                            >
                              <button
                                className="btn-add-cart mobile-cart-btn"
                                disabled={
                                  !inStock || addingToCartId === item.id
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToCart(item);
                                }}
                              >
                                <BsCart3 size={13} />
                                {addingToCartId === item.id
                                  ? "Adding..."
                                  : "Cart"}
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-state-fav">
                <BsHeartbreak className="empty-icon-fav" />
                <h2 className="empty-title-fav">Your wishlist is empty</h2>
                <p className="empty-subtitle-fav">
                  Start adding your favorite items!
                </p>
                <button
                  className="btn-start-shopping-fav"
                  onClick={() => navigate("/categories")}
                >
                  Start Shopping
                </button>
              </div>
            )}
          </div>
        </Container>
      </section>

      <BottomNavigation />
    </>
  );
};

export default FavoritesPage;
